import { observer } from 'mobx-react-lite'
import { useRef, useState } from 'react'
import classNames from 'classnames'
import {
  CalendarDropdown,
  FullPageWrapper,
  Typography,
  SpinnerLoader,
  Tooltip,
  Icon,
  type ActionItem,
  Button,
} from 'shared/ui'
import { secondsToTime } from 'shared/lib/secondsToTime'
import { useElementWidth } from 'shared/hooks'
import { CALLS_METRIC_COLORS } from 'entities/Analytics/lib/constants'
import { type CallsAnalyticsKeys } from 'entities/Analytics'
import { BreakdownFilter } from 'pages/analytics/ui/BreakdownFilter/BreakdownFilter'
import {
  CallsAnalyticsPageProvider,
  useCallsAnalyticsPageContext,
} from 'pages/analytics/pages/calls/context/callsAnalyticsPageContext'
import { AnalyticsHeader, TrendLabel, AnalyticsEmptyState } from 'pages/analytics/ui'
import { ChartFilter } from 'pages/analytics/ui/ChartFilter'
import { CallsLineChart, CallsBarChart } from 'pages/analytics/pages/calls/ui/Chart'
import { CallsComparisonTable } from 'pages/analytics/pages/calls/ui/ComparisonTable'
import styles from './styles.module.scss'

const CallsAnalyticsPageContent = observer(() => {
  const {
    calendarFilterStore,
    breakdownFilterStore,
    chartFilterStore,
    chartDataStore,
    pageLoading,
    chartLoading,
    openExportModal,
    comparisonTableStore,
  } = useCallsAnalyticsPageContext()
  const { clearAllFilters, hasAppliedFilters } = breakdownFilterStore
  const { getStatisticByKey, hasStatistic } = chartDataStore

  const [hiddenMetrics, setHiddenMetrics] = useState<Set<CallsAnalyticsKeys>>(
    new Set([
      'answered_outbound',
      'unanswered_outbound',
      'failed_outbound',
      'busy_outbound',
      'forwarded',
      'answered_inbound_human',
      'answered_inbound_ai',
      'missed_inbound',
      'failed_inbound',
      'busy_inbound',
    ])
  )
  const filtersRowRef = useRef<HTMLDivElement | null>(null)
  const filtersRowWidth = useElementWidth(filtersRowRef)

  const handleToggleMetric = (metricKey: CallsAnalyticsKeys) => {
    setHiddenMetrics((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(metricKey)) {
        newSet.delete(metricKey)
      } else {
        newSet.add(metricKey)
      }
      return newSet
    })
  }

  const totalStat = getStatisticByKey('total')
  const outboundCallsStat = getStatisticByKey('outbound_calls')
  const inboundCallsStat = getStatisticByKey('inbound_calls')
  const avgDurationOutboundStat = getStatisticByKey('avg_duration_outbound')
  const avgDurationInboundStat = getStatisticByKey('avg_duration_inbound')
  const answeredOutboundStat = getStatisticByKey('answered_outbound')
  const unansweredOutboundStat = getStatisticByKey('unanswered_outbound')
  const failedOutboundStat = getStatisticByKey('failed_outbound')
  const busyOutboundStat = getStatisticByKey('busy_outbound')
  const forwardedStat = getStatisticByKey('forwarded')
  const answeredInboundByHumanStat = getStatisticByKey('answered_inbound_human')
  const answeredInboundByAIStat = getStatisticByKey('answered_inbound_ai')
  const missedInboundStat = getStatisticByKey('missed_inbound')
  const failedInboundStat = getStatisticByKey('failed_inbound')
  const busyInboundStat = getStatisticByKey('busy_inbound')

  const exportActions: ActionItem[] = [
    {
      text: 'Export data',
      icon: 'download',
      onAction: openExportModal,
    },
  ]

  const renderContent = () => {
    if (pageLoading) {
      return (
        <div className={styles.fullPageContainer}>
          <SpinnerLoader ariaLabel={'CallsAnalyticsPage'} />
        </div>
      )
    }

    if (!hasStatistic) {
      return (
        <div className={styles.fullPageContainer}>
          <AnalyticsEmptyState />
        </div>
      )
    }

    return (
      <>
        <div className={styles.wrapKpiSection}>
          <div className={styles.kpiSection}>
            <div
              className={classNames(styles.kpiCard, styles.kpiTotalCalls)}
              onClick={() => handleToggleMetric('total')}
            >
              <div
                className={styles.kpiColorBar}
                style={{
                  backgroundColor: hiddenMetrics.has('total')
                    ? 'var(--gray-10)'
                    : CALLS_METRIC_COLORS.total,
                }}
              ></div>
              <div className={styles.kpiCardContent}>
                <Typography
                  variant={'body-rg-regular'}
                  color={'tertiary'}
                  ariaLabel={'TotalCallsTitle'}
                >
                  Total calls
                </Typography>
                <div className={styles.kpiCardMainRow}>
                  <Typography
                    variant={'heading-lg'}
                    color={hiddenMetrics.has('total') ? 'tertiary' : 'initial'}
                    ariaLabel={'TotalCallsValue'}
                  >
                    {totalStat?.value?.toLocaleString() || '0'}
                  </Typography>
                  {totalStat?.trend && <TrendLabel trend={totalStat.trend} displayMode='percent' />}
                </div>
              </div>
            </div>
            <div
              className={classNames(styles.kpiCard, styles.kpiOutboundCalls)}
              onClick={() => handleToggleMetric('outbound_calls')}
            >
              <div
                className={styles.kpiColorBar}
                style={{
                  backgroundColor: hiddenMetrics.has('outbound_calls')
                    ? 'var(--gray-10)'
                    : CALLS_METRIC_COLORS.outbound_calls,
                }}
              ></div>
              <div className={styles.kpiCardContent}>
                <Typography
                  variant={'body-rg-regular'}
                  color={'tertiary'}
                  ariaLabel={'OutboundCallsTitle'}
                >
                  Outbound calls
                </Typography>
                <div className={styles.kpiCardMainRow}>
                  <Typography
                    variant={'heading-lg'}
                    color={hiddenMetrics.has('outbound_calls') ? 'tertiary' : 'initial'}
                    ariaLabel={'OutboundCallsValue'}
                  >
                    {outboundCallsStat?.value?.toLocaleString() || '0'}
                  </Typography>
                  {outboundCallsStat?.trend && (
                    <TrendLabel trend={outboundCallsStat.trend} displayMode='percent' />
                  )}
                </div>
              </div>
            </div>
            <div className={classNames(styles.kpiSubMetricsGrid, styles.kpiOutboundStats)}>
              <div
                className={styles.kpiCard}
                onClick={() => handleToggleMetric('answered_outbound')}
              >
                <div
                  className={styles.kpiColorBar}
                  style={{
                    backgroundColor: hiddenMetrics.has('answered_outbound')
                      ? 'var(--gray-10)'
                      : CALLS_METRIC_COLORS.answered_outbound,
                  }}
                ></div>
                <div className={styles.kpiCardContent}>
                  <div className={styles.kpiCardHeader}>
                    <Typography
                      variant={'body-sm-regular'}
                      color={'tertiary'}
                      ariaLabel={'AnsweredOutboundTitle'}
                    >
                      Answered
                    </Typography>
                  </div>
                  <Typography
                    variant={'heading-sm'}
                    color={hiddenMetrics.has('answered_outbound') ? 'tertiary' : 'initial'}
                    ariaLabel={'AnsweredOutboundValue'}
                  >
                    {answeredOutboundStat?.value?.toLocaleString() || '0'}
                  </Typography>
                </div>
              </div>
              <div
                className={styles.kpiCard}
                onClick={() => handleToggleMetric('unanswered_outbound')}
              >
                <div
                  className={styles.kpiColorBar}
                  style={{
                    backgroundColor: hiddenMetrics.has('unanswered_outbound')
                      ? 'var(--gray-10)'
                      : CALLS_METRIC_COLORS.unanswered_outbound,
                  }}
                ></div>
                <div className={styles.kpiCardContent}>
                  <div className={styles.kpiCardHeader}>
                    <Typography
                      variant={'body-sm-regular'}
                      color={'tertiary'}
                      ariaLabel={'UnansweredOutboundTitle'}
                    >
                      Unanswered
                    </Typography>
                  </div>
                  <Typography
                    variant={'heading-sm'}
                    color={hiddenMetrics.has('unanswered_outbound') ? 'tertiary' : 'initial'}
                    ariaLabel={'UnansweredOutboundValue'}
                  >
                    {unansweredOutboundStat?.value?.toLocaleString() || '0'}
                  </Typography>
                </div>
              </div>
              <div className={styles.kpiCard} onClick={() => handleToggleMetric('failed_outbound')}>
                <div
                  className={styles.kpiColorBar}
                  style={{
                    backgroundColor: hiddenMetrics.has('failed_outbound')
                      ? 'var(--gray-10)'
                      : CALLS_METRIC_COLORS.failed_outbound,
                  }}
                ></div>
                <div className={styles.kpiCardContent}>
                  <div className={styles.kpiCardHeader}>
                    <Typography
                      variant={'body-sm-regular'}
                      color={'tertiary'}
                      ariaLabel={'FailedOutboundTitle'}
                    >
                      Failed
                    </Typography>
                  </div>
                  <Typography
                    variant={'heading-sm'}
                    color={hiddenMetrics.has('failed_outbound') ? 'tertiary' : 'initial'}
                    ariaLabel={'FailedOutboundValue'}
                  >
                    {failedOutboundStat?.value?.toLocaleString() || '0'}
                  </Typography>
                </div>
              </div>
              <div className={styles.kpiCard} onClick={() => handleToggleMetric('busy_outbound')}>
                <div
                  className={styles.kpiColorBar}
                  style={{
                    backgroundColor: hiddenMetrics.has('busy_outbound')
                      ? 'var(--gray-10)'
                      : CALLS_METRIC_COLORS.busy_outbound,
                  }}
                ></div>
                <div className={styles.kpiCardContent}>
                  <div className={styles.kpiCardHeader}>
                    <Typography
                      variant={'body-sm-regular'}
                      color={'tertiary'}
                      ariaLabel={'BusyOutboundTitle'}
                    >
                      Busy
                    </Typography>
                  </div>
                  <Typography
                    variant={'heading-sm'}
                    color={hiddenMetrics.has('busy_outbound') ? 'tertiary' : 'initial'}
                    ariaLabel={'BusyOutboundValue'}
                  >
                    {busyOutboundStat?.value?.toLocaleString() || '0'}
                  </Typography>
                </div>
              </div>
            </div>
            <div className={classNames(styles.kpiCard, styles.kpiAvgDurationOutbound)}>
              <div className={styles.kpiCardContent}>
                <div className={styles.kpiCardHeader}>
                  <Typography
                    variant={'body-sm-regular'}
                    color={'tertiary'}
                    ariaLabel={'AvgDurationOutboundTitle'}
                  >
                    Avg. duration
                  </Typography>
                  <Tooltip label='Time format is minutes:seconds.' placement='top'>
                    <Icon icon='question' color='var(--gray-40)' />
                  </Tooltip>
                </div>
                <Typography
                  variant={'heading-sm'}
                  color={hiddenMetrics.has('avg_duration_outbound') ? 'tertiary' : 'initial'}
                  ariaLabel={'AvgDurationOutboundValue'}
                >
                  {secondsToTime((avgDurationOutboundStat?.value ?? 0) * 60)}
                </Typography>
              </div>
            </div>
            <div
              className={classNames(styles.kpiCard, styles.kpiInboundCalls)}
              onClick={() => handleToggleMetric('inbound_calls')}
            >
              <div
                className={styles.kpiColorBar}
                style={{
                  backgroundColor: hiddenMetrics.has('inbound_calls')
                    ? 'var(--gray-10)'
                    : CALLS_METRIC_COLORS.inbound_calls,
                }}
              ></div>
              <div className={styles.kpiCardContent}>
                <Typography
                  variant={'body-rg-regular'}
                  color={'tertiary'}
                  ariaLabel={'InboundCallsTitle'}
                >
                  Inbound calls
                </Typography>
                <div className={styles.kpiCardMainRow}>
                  <Typography
                    variant={'heading-lg'}
                    color={hiddenMetrics.has('inbound_calls') ? 'tertiary' : 'initial'}
                    ariaLabel={'InboundCallsValue'}
                  >
                    {inboundCallsStat?.value?.toLocaleString() || '0'}
                  </Typography>
                  {inboundCallsStat?.trend && (
                    <TrendLabel trend={inboundCallsStat.trend} displayMode='percent' />
                  )}
                </div>
              </div>
            </div>
            <div className={classNames(styles.kpiSubMetricsGrid, styles.kpiInboundStats)}>
              <div
                className={styles.kpiCard}
                onClick={() => handleToggleMetric('answered_inbound_human')}
              >
                <div
                  className={styles.kpiColorBar}
                  style={{
                    backgroundColor: hiddenMetrics.has('answered_inbound_human')
                      ? 'var(--gray-10)'
                      : CALLS_METRIC_COLORS.answered_inbound_human,
                  }}
                ></div>
                <div className={styles.kpiCardContent}>
                  <div className={styles.kpiCardHeader}>
                    <Typography
                      variant={'body-sm-regular'}
                      color={'tertiary'}
                      ariaLabel={'AnsweredInboundByHumanTitle'}
                    >
                      {answeredInboundByHumanStat?.label || 'Answered by human'}
                    </Typography>
                  </div>
                  <Typography
                    variant={'heading-sm'}
                    color={hiddenMetrics.has('answered_inbound_human') ? 'tertiary' : 'initial'}
                    ariaLabel={'AnsweredInboundByHumanValue'}
                  >
                    {answeredInboundByHumanStat?.value?.toLocaleString() || '0'}
                  </Typography>
                </div>
              </div>

              <div
                className={styles.kpiCard}
                onClick={() => handleToggleMetric('answered_inbound_ai')}
              >
                <div
                  className={styles.kpiColorBar}
                  style={{
                    backgroundColor: hiddenMetrics.has('answered_inbound_ai')
                      ? 'var(--gray-10)'
                      : CALLS_METRIC_COLORS.answered_inbound_ai,
                  }}
                ></div>
                <div className={styles.kpiCardContent}>
                  <div className={styles.kpiCardHeader}>
                    <Typography
                      variant={'body-sm-regular'}
                      color={'tertiary'}
                      ariaLabel={'AnsweredInboundByAITitle'}
                    >
                      {answeredInboundByAIStat?.label || 'Answered by AI'}
                    </Typography>
                  </div>
                  <Typography
                    variant={'heading-sm'}
                    color={hiddenMetrics.has('answered_inbound_ai') ? 'tertiary' : 'initial'}
                    ariaLabel={'AnsweredInboundByAIValue'}
                  >
                    {answeredInboundByAIStat?.value?.toLocaleString() || '0'}
                  </Typography>
                </div>
              </div>

              <div className={styles.kpiCard} onClick={() => handleToggleMetric('missed_inbound')}>
                <div
                  className={styles.kpiColorBar}
                  style={{
                    backgroundColor: hiddenMetrics.has('missed_inbound')
                      ? 'var(--gray-10)'
                      : CALLS_METRIC_COLORS.missed_inbound,
                  }}
                ></div>
                <div className={styles.kpiCardContent}>
                  <div className={styles.kpiCardHeader}>
                    <Typography
                      variant={'body-sm-regular'}
                      color={'tertiary'}
                      ariaLabel={'MissedInboundTitle'}
                    >
                      Missed
                    </Typography>
                  </div>
                  <Typography
                    variant={'heading-sm'}
                    color={hiddenMetrics.has('missed_inbound') ? 'tertiary' : 'initial'}
                    ariaLabel={'MissedInboundValue'}
                  >
                    {missedInboundStat?.value?.toLocaleString() || '0'}
                  </Typography>
                </div>
              </div>

              <div className={styles.kpiCard} onClick={() => handleToggleMetric('failed_inbound')}>
                <div
                  className={styles.kpiColorBar}
                  style={{
                    backgroundColor: hiddenMetrics.has('failed_inbound')
                      ? 'var(--gray-10)'
                      : CALLS_METRIC_COLORS.failed_inbound,
                  }}
                ></div>
                <div className={styles.kpiCardContent}>
                  <div className={styles.kpiCardHeader}>
                    <Typography
                      variant={'body-sm-regular'}
                      color={'tertiary'}
                      ariaLabel={'FailedInboundTitle'}
                    >
                      Failed
                    </Typography>
                  </div>
                  <Typography
                    variant={'heading-sm'}
                    color={hiddenMetrics.has('failed_inbound') ? 'tertiary' : 'initial'}
                    ariaLabel={'FailedInboundValue'}
                  >
                    {failedInboundStat?.value?.toLocaleString() || '0'}
                  </Typography>
                </div>
              </div>

              <div className={styles.kpiCard} onClick={() => handleToggleMetric('busy_inbound')}>
                <div
                  className={styles.kpiColorBar}
                  style={{
                    backgroundColor: hiddenMetrics.has('busy_inbound')
                      ? 'var(--gray-10)'
                      : CALLS_METRIC_COLORS.busy_inbound,
                  }}
                ></div>
                <div className={styles.kpiCardContent}>
                  <div className={styles.kpiCardHeader}>
                    <Typography
                      variant={'body-sm-regular'}
                      color={'tertiary'}
                      ariaLabel={'BusyInboundTitle'}
                    >
                      Busy
                    </Typography>
                  </div>
                  <Typography
                    variant={'heading-sm'}
                    color={hiddenMetrics.has('busy_inbound') ? 'tertiary' : 'initial'}
                    ariaLabel={'BusyInboundValue'}
                  >
                    {busyInboundStat?.value?.toLocaleString() || '0'}
                  </Typography>
                </div>
              </div>

              <div className={styles.kpiCard} onClick={() => handleToggleMetric('forwarded')}>
                <div
                  className={styles.kpiColorBar}
                  style={{
                    backgroundColor: hiddenMetrics.has('forwarded')
                      ? 'var(--gray-10)'
                      : CALLS_METRIC_COLORS.forwarded,
                  }}
                ></div>
                <div className={styles.kpiCardContent}>
                  <div className={styles.kpiCardHeader}>
                    <Typography
                      variant={'body-sm-regular'}
                      color={'tertiary'}
                      ariaLabel={'ForwardedTitle'}
                    >
                      Forwarded
                    </Typography>
                  </div>
                  <Typography
                    variant={'heading-sm'}
                    color={hiddenMetrics.has('forwarded') ? 'tertiary' : 'initial'}
                    ariaLabel={'ForwardedValue'}
                  >
                    {forwardedStat?.value?.toLocaleString() || '0'}
                  </Typography>
                </div>
              </div>
            </div>
            <div className={classNames(styles.kpiCard, styles.kpiAvgDurationInbound)}>
              <div className={styles.kpiCardContent}>
                <div className={styles.kpiCardHeader}>
                  <Typography
                    variant={'body-sm-regular'}
                    color={'tertiary'}
                    ariaLabel={'AvgDurationInboundTitle'}
                  >
                    Avg. duration
                  </Typography>
                  <Tooltip label='Time format is minutes:seconds.' placement='top'>
                    <Icon icon='question' color='var(--gray-40)' />
                  </Tooltip>
                </div>
                <Typography
                  variant={'heading-sm'}
                  color={hiddenMetrics.has('avg_duration_inbound') ? 'tertiary' : 'initial'}
                  ariaLabel={'AvgDurationInboundValue'}
                >
                  {secondsToTime((avgDurationInboundStat?.value ?? 0) * 60)}
                </Typography>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.chartSection}>
          <div className={styles.chartHeader}>
            <ChartFilter store={chartFilterStore} />
          </div>
          <div className={styles.chartArea}>
            {chartLoading ? (
              <SpinnerLoader ariaLabel={'CallsAnalyticsPage'} />
            ) : (
              <>
                {chartFilterStore.chartType === 'line' && (
                  <CallsLineChart chartDataStore={chartDataStore} hiddenMetrics={hiddenMetrics} />
                )}
                {chartFilterStore.chartType === 'bar' && (
                  <CallsBarChart chartDataStore={chartDataStore} hiddenMetrics={hiddenMetrics} />
                )}
              </>
            )}
          </div>
        </div>

        {hasAppliedFilters && (
          <div className={styles.tableSection}>
            <CallsComparisonTable store={comparisonTableStore} />
          </div>
        )}
      </>
    )
  }

  return (
    <FullPageWrapper className={styles.page}>
      <AnalyticsHeader title='Calls' actions={exportActions} />
      <div className={styles.filtersRow} ref={filtersRowRef}>
        <CalendarDropdown store={calendarFilterStore} />
        <BreakdownFilter store={breakdownFilterStore} parentWidth={filtersRowWidth} />
        {hasAppliedFilters && (
          <Button
            contained='secondary'
            color={'var(--content-primary-secondary)'}
            typeBtn='text'
            text='Clear all'
            onClick={clearAllFilters}
          />
        )}
      </div>
      {renderContent()}
    </FullPageWrapper>
  )
})

export const CallsAnalyticsPage = () => (
  <CallsAnalyticsPageProvider>
    <CallsAnalyticsPageContent />
  </CallsAnalyticsPageProvider>
)
